import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { IconName } from '@skylab-assets/icons';
import { IconComponent } from '../icon/icon.component';

@Component({
	selector: 'skl-card',
	template: `
		<button
			type="button"
			class="card"
			(click)="clicked.emit()"
		>
			@if (icon()) {
				<span class="card-icon" aria-hidden="true">
					<skl-icon [name]="icon()" size="24px" />
				</span>
			}
			<section class="card-content">
				<header class="card-title">
					<ng-content select="[slot=title]" />
				</header>
				<article class="card-description">
					<ng-content select="[slot=description]" />
				</article>
			</section>
		</button>
	`,
	styles: `
		:host {
			--focus-outline: var(--primary-color-light);

			--_card-background-color: light-dark(var(--surface-3-color), var(--surface-2-color));
			--_card-border-color: light-dark(var(--line-1-color), transparent);
			--_card-icon-background-color: light-dark(var(--surface-1-color), var(--surface-4-color));
			--_card-icon-color: var(--text-minor-color);

			display: contents;

			button {
				appearance: none;
				font-family: inherit;
				font-size: 100%;
				text-align: left;
				color: inherit;
				margin: 0;
			}

		}

		.card {
			display: flex;
			flex-wrap: wrap;
			background-color: var(--_card-background-color); 
			border: 1px solid var(--_card-border-color);
			border-radius: var(--gutter-small);
			padding: var(--gutter-medium);
			gap: var(--gutter-medium);
			transition: all 0.3s cubic-bezier(0.66, 0, 0.34, 1);
			outline-offset: -2px;
			align-items: center;
			cursor: pointer;

			&:hover,
			&:focus-visible {
				--_card-background-color: var(--surface-3-color);
				--_card-border-color: var(--line-1-color);
				--_card-icon-color: var(--text-color);

				outline: none;

				transform: translateY(-2px);
				box-shadow:
					0 4px 6px -1px rgb(0 0 0 / 10%),
					0 2px 4px -1px rgb(0 0 0 / 6%);

				@media (prefers-reduced-motion: reduce) {
					transform: none;
				}
			}

			&:focus-visible {
				outline-offset: .1875rem;
				outline: 2px solid var(--focus-outline);
			}

			.card-icon {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 3rem;
				aspect-ratio: 1;
				color: var(--_card-icon-color);
				background-color: var(--_card-icon-background-color);
				border-radius: var(--gutter-small);
				flex-shrink: 0;
			}

			.card-content {
				display: flex;
				flex: 1;
				flex-direction: column;
				gap: var(--gutter-tiny);

				.card-title {
					font-size: 1rem;
					font-weight: 500;
					line-height: 1.5;
					text-wrap: balance;
					letter-spacing: -0.02rem;
					color: var(--text-color);
				}

				.card-description {
					font-size: .8rem;
					letter-spacing: 0.03rem;
					color: var(--text-minor-color);
					line-height: 1.5;
					text-wrap: pretty;
				}
			}
		}
	`,
	imports: [IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent {
	readonly icon = input.required<IconName | undefined>();
	readonly clicked = output<void>();
}
